/** Calendar semantics are Belgian; stored instants remain UTC. */
export const SITE_TIME_ZONE = "Europe/Brussels";

export function formatSiteDate(value: string | Date, locale = "nl-BE", options: Intl.DateTimeFormatOptions = { dateStyle: "medium" }): string {
  return new Intl.DateTimeFormat(locale, { ...options, timeZone: SITE_TIME_ZONE }).format(new Date(value));
}

export function formatSiteDateTime(value: string | Date, locale = "nl-BE"): string {
  return formatSiteDate(value, locale, { dateStyle: "short", timeStyle: "short" });
}

export function siteDay(value: string | Date = new Date()): string {
  const parts = new Intl.DateTimeFormat("en-GB", { timeZone: SITE_TIME_ZONE, year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(new Date(value));
  const part = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  return `${part("year")}-${part("month")}-${part("day")}`;
}

/** Shift a calendar date without applying the runtime's time zone. */
export function shiftDay(day: string, days: number): string {
  const date = new Date(`${day}T12:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

/** Resolve a Brussels wall-clock time to an instant, including DST. */
export function siteInstant(day: string, hour = 0, minute = 0): Date {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day) || !Number.isInteger(hour) || hour < 0 || hour > 23 || !Number.isInteger(minute) || minute < 0 || minute > 59) throw new Error("Invalid calendar date");
  const target = Date.parse(`${day}T${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}:00Z`);
  if (!Number.isFinite(target) || new Date(target).toISOString().slice(0, 10) !== day) throw new Error("Invalid calendar date");
  let instant = target;
  const formatter = new Intl.DateTimeFormat("en-GB", { timeZone: SITE_TIME_ZONE, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit", hourCycle: "h23" });
  for (let i = 0; i < 3; i += 1) {
    const parts = formatter.formatToParts(new Date(instant));
    const part = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
    const wall = Date.parse(`${part("year")}-${part("month")}-${part("day")}T${part("hour")}:${part("minute")}:${part("second")}Z`);
    instant += target - wall;
  }
  return new Date(instant);
}

export function siteBucket(value: string | Date, granularity: "day" | "week" | "month"): string {
  const day = siteDay(value);
  if (granularity === "month") return day.slice(0, 7);
  if (granularity === "day") return day;
  return shiftDay(day, -((new Date(`${day}T12:00:00Z`).getUTCDay() + 6) % 7));
}

export function siteMonthRange(offset = 0, now = new Date()): { start: string; end: string; ym: string } {
  const month = new Date(`${siteDay(now).slice(0, 7)}-01T12:00:00Z`);
  month.setUTCMonth(month.getUTCMonth() - offset);
  const first = month.toISOString().slice(0, 10);
  month.setUTCMonth(month.getUTCMonth() + 1);
  return { start: siteInstant(first).toISOString(), end: siteInstant(month.toISOString().slice(0, 10)).toISOString(), ym: first.slice(0, 7) };
}

/** Match calendar-day counts across DST; keep the same local end time. */
export function previousSiteRange(from: string, to: string): string {
  const first = siteDay(from);
  const last = siteDay(to);
  const days = Math.round((Date.parse(`${last}T12:00:00Z`) - Date.parse(`${first}T12:00:00Z`)) / 86400000);
  const midnight = siteInstant(first);
  if (Date.parse(from) !== midnight.getTime()) return new Date(Date.parse(from) - (Date.parse(to) - Date.parse(from))).toISOString();
  return siteInstant(shiftDay(first, -(days + (Date.parse(to) > siteInstant(last).getTime() ? 1 : 0)))).toISOString();
}